// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Hub nav derivation only
// 2. Separation of Concerns - Permission + fixed system settings separate from API
// 3. Modular Architecture - Reusable from App bar and Settings hub
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import { FIXED_SYSTEM_SETTINGS_HUB_ENTRIES } from './fixedSystemSettingsNav';

/**
 * Maps system_settings.category (URL segment) to READ permission codes.
 * Must stay aligned with backend SETTING_CATEGORY_READ (settings_category_permissions.py).
 */
export const SETTINGS_CATEGORY_READ_PERMISSION = {
  qa_context: 'QA_CONTEXT_SETTINGS_READ',
  translation_glossary: 'TRANSLATION_GLOSSARY_READ',
  voice_processing: 'VOICE_PROCESSING_SETTINGS_READ',
  word_correction: 'WORD_CORRECTION_READ',
};

/** Aligned with backend SETTING_CATEGORY_UPDATE (settings_category_permissions.py). */
export const SETTINGS_CATEGORY_UPDATE_PERMISSION = {
  qa_context: 'QA_CONTEXT_SETTINGS_UPDATE',
  translation_glossary: 'TRANSLATION_GLOSSARY_UPDATE',
  voice_processing: 'VOICE_PROCESSING_SETTINGS_UPDATE',
  word_correction: 'WORD_CORRECTION_UPDATE',
};

/**
 * Build ordered hub nav items from RBAC only. System setting categories are fixed
 * in fixedSystemSettingsNav.js (not from getCategories API).
 *
 * @param {(code: string) => boolean} hasPermission
 * @returns {Array<{ path: string, label: string, category?: string, kind: string, tabIconKind?: string }>}
 */
export function computeSettingsHubNavItems(hasPermission) {
  const items = [];

  if (hasPermission('CHAT_PROMPTS_READ')) {
    items.push({
      path: '/chat-prompts',
      label: 'Chat Prompts Settings',
      kind: 'chat_prompts',
      tabIconKind: 'chat_prompts',
    });
  }
  if (hasPermission('QA_PROMPTS_READ')) {
    items.push({
      path: '/qa-prompts',
      label: 'QA Prompts Settings',
      kind: 'qa_prompts',
      tabIconKind: 'qa_prompts',
    });
  }
  if (hasPermission('WORD_CORRECTION_READ')) {
    items.push({
      path: '/word-correction',
      label: 'Word Correction',
      kind: 'word_correction',
      tabIconKind: 'word_correction',
    });
  }

  FIXED_SYSTEM_SETTINGS_HUB_ENTRIES.forEach((entry) => {
    if (hasPermission(entry.readPermission)) {
      items.push({
        path: `/settings-category/${encodeURIComponent(entry.systemCategory)}`,
        label: entry.label,
        category: entry.systemCategory,
        kind: 'settings_category',
        tabIconKind: entry.tabIconKind,
      });
    }
  });

  return items;
}
